import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTheme } from '../../contexts/TenantThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Tag, ArrowRight, ChevronDown, ChevronUp, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../../services/api';
import SafeImage from '../../components/SafeImage';

const LOGO_DEFAULT = '/assets/juwa-us-logo.jpg';
const PLATFORM_DOMAIN = (process.env.REACT_APP_PLATFORM_DOMAIN || '').toLowerCase();
const SIGNUP_EMAIL_VERIFICATION_ENABLED =
  process.env.REACT_APP_SIGNUP_EMAIL_VERIFICATION_ENABLED !== 'false';

const defaultNameFromEmail = (value = '') => {
  const prefix = String(value || '').split('@')[0] || '';
  const first = prefix.match(/[a-z0-9]/i)?.[0] || '';
  return first ? first.toUpperCase() : 'Player';
};

const getTenantHostSlug = () => {
  const host = window.location.hostname || '';
  const parts = host.split('.').filter(Boolean);
  if (PLATFORM_DOMAIN && parts.length >= 3 && parts.slice(-2).join('.') === PLATFORM_DOMAIN) {
    const subdomain = parts[0].toLowerCase();
    if (!['www', 'api', 'admin'].includes(subdomain)) return subdomain;
  }
  return null;
};

// Default Juwa.US palette — used as fallback when no tenant
// theme is set. Every value gets overridden by `useTenantTheme()`.
const DEFAULT_THEME = {
  bg: '#0A0519',
  purple: '#9C47E0',
  purpleSoft: 'rgba(156,71,224,0.30)',
  purpleGlow: 'rgba(156,71,224,0.08)',
  gold: '#FFD700',
  white: '#FFFFFF',
  dim: '#A0A0A0',
  card: 'rgba(156,71,224,0.08)',
};

export default function AuthPage({ portal = 'player' }) {
  const { user, login, register, tenantSlug: authTenantSlug } = useAuth();
  const { tenantSlug: paramTenantSlug } = useParams();        // present on /:tenantSlug/auth
  const tenantSlug = paramTenantSlug || authTenantSlug;
  const { theme: tenantTheme } = useTenantTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isAdminPortal = portal === 'admin';
  const hostTenantSlug = getTenantHostSlug();
  const isMainPortal = isAdminPortal && !paramTenantSlug && !hostTenantSlug;
  const activeTenantSlug = isMainPortal ? null : (paramTenantSlug || hostTenantSlug || tenantSlug);
  const referralParam =
    searchParams.get('ref') ||
    searchParams.get('referral') ||
    searchParams.get('referral_code') ||
    searchParams.get('code') ||
    '';
  const shouldOpenSignup = !isAdminPortal && (searchParams.get('mode') === 'register' || Boolean(referralParam));
  const [mode, setMode] = useState(shouldOpenSignup ? 'register' : 'login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Referral code
  const [referralOpen, setReferralOpen] = useState(Boolean(referralParam));
  const [referralCode, setReferralCode] = useState(referralParam);
  const [referralStatus, setReferralStatus] = useState(null); // null | {valid, referrer_username, referrer_tier, message}
  const [referralChecking, setReferralChecking] = useState(false);

  // Email verification (signup) flow
  const [signupStep, setSignupStep] = useState('form'); // 'form' | 'verify'
  const [verifyCode, setVerifyCode] = useState('');

  // Forgot password flow
  const [forgot, setForgot] = useState({ open: false, step: 'email', email: '', code: '', newPassword: '' });

  // Branding
  const [brand, setBrand] = useState({ brand_name: '', logo_url: '', primary_color: '#A06CD5' });
  useEffect(() => {
    api.get('/api/branding').then(r => setBrand(b => ({ ...b, ...(r.data || {}) }))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    // Route by role — admins/moderators/support → /admin, staff → /staff-portal,
    // everyone else (player) → /app. Tenant URLs preserve their slug prefix
    // so a morphues player lands at /morphues/app (not /app).
    const role = user.role;
    const slugPrefix = activeTenantSlug ? `/${activeTenantSlug}` : '';
    if (['admin', 'moderator', 'support'].includes(role)) {
      navigate(`${slugPrefix}/admin`, { replace: true });
    } else if (role === 'staff') {
      navigate(`${slugPrefix}/staff-portal`, { replace: true });
    } else {
      navigate(`${slugPrefix}/app`, { replace: true });
    }
  }, [user, navigate, activeTenantSlug]);

  useEffect(() => {
    if (isAdminPortal && mode !== 'login') {
      setMode('login');
      setSignupStep('form');
      setVerifyCode('');
    }
  }, [isAdminPortal, mode]);

  useEffect(() => {
    if (isAdminPortal) return;
    const nextReferral =
      searchParams.get('ref') ||
      searchParams.get('referral') ||
      searchParams.get('referral_code') ||
      searchParams.get('code') ||
      '';

    if (nextReferral) {
      setMode('register');
      setSignupStep('form');
      setVerifyCode('');
      setReferralOpen(true);
      setReferralCode(nextReferral);
      return;
    }

    if (searchParams.get('mode') === 'register') {
      setMode('register');
      setSignupStep('form');
    }
  }, [isAdminPortal, searchParams]);

  const validateReferral = async () => {
    const code = (referralCode || '').trim();
    if (!code) {
      toast.error('Enter a referral code first');
      return;
    }
    setReferralChecking(true);
    setReferralStatus(null);
    try {
      const r = await api.get(`/api/auth/referral/validate`, { params: { code } });
      setReferralStatus(r.data || { valid: false });
      if (r.data?.valid) {
        toast.success(`Code applied — referred by ${r.data.referrer_username}`);
      } else {
        toast.error(r.data?.message || 'Invalid referral code');
      }
    } catch (e) {
      setReferralStatus({ valid: false, message: 'Could not validate code' });
      toast.error('Could not validate code');
    } finally {
      setReferralChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Enter your email and password');
      return;
    }
    if (mode === 'register' && password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password, activeTenantSlug, isAdminPortal ? 'admin' : 'player');
        toast.success(isAdminPortal ? 'Admin access granted!' : 'Welcome back!');
      } else if (signupStep === 'form' && SIGNUP_EMAIL_VERIFICATION_ENABLED) {
        await api.post('/api/auth/signup/email/send-otp', { email });
        setSignupStep('verify');
        toast.success('We sent a 6-digit code to your email.');
      } else {
        if (SIGNUP_EMAIL_VERIFICATION_ENABLED && (!verifyCode || verifyCode.length !== 6)) {
          toast.error('Enter the 6-digit code from your email');
          return;
        }
        await register(
          email,
          password,
          defaultNameFromEmail(email),
          null,
          referralCode,
          activeTenantSlug,
          SIGNUP_EMAIL_VERIFICATION_ENABLED ? verifyCode : null,
        );
        toast.success(`Account created! Welcome to ${tenantTheme.brand_name || brand.brand_name || 'the lobby'}!`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const backendMessage = err.response?.data?.message;
      const message = typeof detail === 'string' ? detail :
                      typeof backendMessage === 'string' ? backendMessage :
                      Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                      'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Resolution order (theme tokens FIRST so admin's saved theme always wins
  // over the legacy /api/branding `primary_color` field, which is older and
  // not editable from the new Theme Editor):
  //   theme.btn_primary_from → theme.neon → branding.primary_color → default
  const primary =
    (isMainPortal ? '#D4AF37' : null) ||
    tenantTheme.btn_primary_from ||
    tenantTheme.neon ||
    brand.primary_color ||
    DEFAULT_THEME.purple;
  const primaryTo =
    (isMainPortal ? '#F5D76E' : null) ||
    tenantTheme.btn_primary_to ||
    tenantTheme.neon ||
    brand.primary_color ||
    DEFAULT_THEME.purple;
  const primaryText = isMainPortal ? '#06070A' : (tenantTheme.btn_primary_text || '#000000');
  const authAccent =
    (isMainPortal ? '#D4AF37' : null) ||
    tenantTheme.btn_warning_from ||
    tenantTheme.profile_avatar_ring ||
    tenantTheme.header_accent ||
    DEFAULT_THEME.gold;
  const isSignup = mode === 'register';
  const brandName = isMainPortal ? 'MAIN PORTAL' : (tenantTheme.brand_name || brand.brand_name || '');
  const brandLogo = isMainPortal ? '' : (tenantTheme.logo_url || tenantTheme.intro_logo_url || brand.logo_url || '');

  // Submit / primary CTA gradient — applied to Sign In, Create Account,
  // Verify & Create, and the Forgot-Password reset CTAs.
  const PRIMARY_GRADIENT = `linear-gradient(135deg, ${primary} 0%, ${primaryTo} 100%)`;

  // Resolve THEME from tenant-saved values, falling back to platform defaults.
  // Every place in the JSX that used to reference `THEME.x` reads from this
  // object, so a tenant's saved colors instantly re-skin the auth page.
  const THEME = {
    ...DEFAULT_THEME,
    bg: isMainPortal ? '#07090F' : (tenantTheme.bg || DEFAULT_THEME.bg),
    purple: authAccent,
    purpleSoft: `color-mix(in oklab, ${authAccent} 30%, transparent)`,
    purpleGlow: `color-mix(in oklab, ${authAccent} 8%, transparent)`,
    card:       `color-mix(in oklab, ${authAccent} 8%, transparent)`,
    dim: isMainPortal ? '#A7ADBA' : (tenantTheme.text_muted || DEFAULT_THEME.dim),
    white: isMainPortal ? '#FFFFFF' : (tenantTheme.text_primary || DEFAULT_THEME.white),
    gold: isMainPortal ? '#D4AF37' : (tenantTheme.btn_warning_from || DEFAULT_THEME.gold),
    primaryText,
  };

  return (
    <div
      className="min-h-screen text-white flex flex-col items-center justify-start px-5 py-6"
      style={{ background: THEME.bg }}
      data-testid="auth-page"
    >
      <div className="w-full max-w-sm mx-auto">
        {/* Logo */}
        {/* Logo — prefer tenant logo, fall back to platform logo. If a
            tenant has no logo uploaded, show its brand name as a styled
            text mark instead of a cross-tenant brand mark. */}
        <div className="flex flex-col items-center pt-1 mb-4" data-testid="auth-logo">
          {isMainPortal ? (
            <div className="text-center mb-2">
              <img src="/assets/juwa-us-logo.jpg" alt="Juwa.US" className="h-24 w-24 mx-auto rounded-2xl object-cover shadow-2xl" />
              <p className="text-2xl font-extrabold tracking-tight text-white mt-1">
                Main Portal Login
              </p>
            </div>
          ) : brandLogo ? (
            <SafeImage
              src={brandLogo}
              alt={brandName || 'Brand logo'}
              className="w-40 h-40 object-contain select-none"
              draggable={false}
              loading="eager"
              fallback={tenantSlug ? (
                <div
                  className="w-32 h-32 rounded-3xl flex items-center justify-center text-3xl font-extrabold tracking-wide select-none mb-1"
                  style={{
                    background: `linear-gradient(135deg, ${THEME.purple}, color-mix(in oklab, ${THEME.purple} 50%, black))`,
                    color: THEME.primaryText,
                    boxShadow: `0 18px 38px ${THEME.purpleSoft}, inset 0 1px 0 rgba(255,255,255,0.18)`,
                    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                  }}
                >
                  {(brandName || 'B').charAt(0).toUpperCase()}
                </div>
              ) : null}
            />
          ) : tenantSlug ? (
            <div
              className="w-32 h-32 rounded-3xl flex items-center justify-center text-3xl font-extrabold tracking-wide select-none mb-1"
              style={{
                background: `linear-gradient(135deg, ${THEME.purple}, color-mix(in oklab, ${THEME.purple} 50%, black))`,
                color: THEME.primaryText,
                boxShadow: `0 18px 38px ${THEME.purpleSoft}, inset 0 1px 0 rgba(255,255,255,0.18)`,
                fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              }}
            >
              {(brandName || 'B').charAt(0).toUpperCase()}
            </div>
          ) : (
            <SafeImage
              src={LOGO_DEFAULT}
              alt="Juwa.US"
              className="w-40 h-40 object-contain select-none"
              draggable={false}
              loading="eager"
              fallback={null}
            />
          )}
          {!isMainPortal && tenantSlug && brandName && (
            <p className="display text-base font-extrabold uppercase tracking-[0.2em] mt-2" style={{ color: THEME.white }}>
              {brandName}
            </p>
          )}
        </div>

        {/* Heading */}
        <h1 className="text-[28px] font-extrabold text-center text-white leading-tight" data-testid="auth-title">
          {isMainPortal ? 'Main Portal Login' : (isAdminPortal ? 'Admin Sign In' : (isSignup ? 'Create Account' : 'Welcome Back'))}
        </h1>
        <p className="text-center text-sm mt-1.5 mb-6" style={{ color: THEME.dim }}>
          {isMainPortal ? 'Manage tenants, platform settings, and operations' : (isAdminPortal ? 'Use your admin ID and password' : (isSignup ? 'Join and start playing in seconds' : 'Sign in to continue your lobby journey'))}
        </p>

        {/* Signup OTP step */}
        {isSignup && signupStep === 'verify' ? (
          <div className="space-y-4">
            <div className="rounded-2xl border p-4" style={{ borderColor: THEME.purple, background: THEME.purpleGlow }}>
              <p className="text-sm text-gray-300">We sent a 6-digit code to</p>
              <p className="text-white font-bold break-all">{email}</p>
            </div>
            <label className="block text-sm text-gray-400">Enter verification code</label>
            <Input
              data-testid="verify-code-input"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              placeholder="• • • • • •"
              className="text-center tracking-[0.6em] text-2xl bg-transparent border-2 text-white h-14 rounded-xl"
              style={{ borderColor: THEME.purple }}
            />
            <div className="flex justify-between items-center text-xs">
              <button type="button" onClick={() => { setSignupStep('form'); setVerifyCode(''); }} className="text-gray-400 hover:text-white underline">← Change email</button>
              <button type="button" onClick={async () => { try { await api.post('/api/auth/signup/email/send-otp', { email }); toast.success('New code sent'); } catch { toast.error('Resend failed'); } }} className="font-semibold" style={{ color: THEME.purple }} data-testid="resend-code-btn">Resend code</button>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={loading || verifyCode.length !== 6}
              className="w-full h-14 rounded-2xl font-bold transition disabled:opacity-60"
              style={{ background: PRIMARY_GRADIENT, color: primaryText, fontSize: '1.05rem' }}
              data-testid="submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify &amp; Create Account <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: THEME.purple }} />
              <Input
                data-testid="email-input"
                type={isSignup ? 'email' : 'text'}
                autoComplete={isSignup ? 'email' : 'username'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={isAdminPortal ? 'Admin ID or Email' : (isSignup ? 'Email' : 'Email or Username')}
                className="pl-12 h-14 rounded-2xl border-2 bg-transparent text-white placeholder:text-gray-500"
                style={{ borderColor: THEME.purple }}
                required
              />
            </div>

            {/* Password */}
            <div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: THEME.purple }} />
                <Input
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isSignup ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="pl-12 pr-12 h-14 rounded-2xl border-2 bg-transparent text-white placeholder:text-gray-500"
                  style={{ borderColor: THEME.purple }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  data-testid="password-toggle"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" style={{ color: THEME.white }} /> : <Eye className="w-5 h-5" style={{ color: THEME.white }} />}
                </button>
              </div>
              {isSignup && <p className="text-xs text-gray-500 mt-1.5">Minimum 6 characters</p>}
            </div>

            {/* Referral code — collapsible (signup only) */}
            {isSignup && (
              <div
                className="rounded-2xl border transition-all overflow-hidden"
                style={{
                  borderColor: referralOpen ? THEME.purple : THEME.purpleSoft,
                  background: referralOpen ? THEME.purpleGlow : 'transparent',
                }}
              >
                <button
                  type="button"
                  onClick={() => setReferralOpen(o => !o)}
                  className="w-full flex items-center justify-between p-4"
                  data-testid="referral-toggle-btn"
                >
                  <span className="flex items-center gap-3">
                    <Tag className="w-5 h-5" style={{ color: THEME.purple }} />
                    <span className="text-white font-medium">Have a referral code?</span>
                  </span>
                  {referralOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                {referralOpen && (
                  <div className="p-4 pt-0 space-y-3">
                    <p className="text-xs text-gray-400">Enter referral code (optional)</p>
                    <div className="relative">
                      <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: THEME.purple }} />
                      <Input
                        data-testid="referral-input"
                        type="text"
                        value={referralCode}
                        onChange={(e) => { setReferralCode(e.target.value.toUpperCase()); setReferralStatus(null); }}
                        placeholder="Enter or paste code"
                        className="pl-10 h-12 rounded-xl border-2 bg-transparent text-white placeholder:text-gray-500 tracking-wider"
                        style={{ borderColor: THEME.purple }}
                      />
                    </div>
                    <p className="text-xs text-gray-400">You will get rewards for joining with a referral code.</p>

                    {/* Referral status */}
                    {referralStatus && referralStatus.valid && (
                      <div
                        className="rounded-xl p-3 flex items-center gap-3"
                        style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.4)' }}
                        data-testid="referral-status-success"
                      >
                        <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="font-bold text-green-300 text-sm">Referral code applied!</p>
                          <p className="text-xs text-gray-300">You'll be referred by <span className="text-green-300 font-semibold" data-testid="referrer-username">{referralStatus.referrer_username}</span>{referralStatus.referrer_tier ? ` · ${referralStatus.referrer_tier}` : ''}</p>
                        </div>
                      </div>
                    )}
                    {referralStatus && !referralStatus.valid && (
                      <div
                        className="rounded-xl p-3 flex items-center gap-3"
                        style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)' }}
                        data-testid="referral-status-error"
                      >
                        <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <p className="text-sm text-red-300">{referralStatus.message || 'Invalid referral code'}</p>
                      </div>
                    )}

                    <Button
                      type="button"
                      onClick={validateReferral}
                      disabled={referralChecking || !referralCode.trim()}
                      className="w-full h-12 rounded-xl font-semibold disabled:opacity-50"
                      style={{ background: THEME.purple, color: '#fff' }}
                      data-testid="apply-code-btn"
                    >
                      {referralChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply Code'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-2xl font-extrabold transition disabled:opacity-60"
              style={{ background: PRIMARY_GRADIENT, color: primaryText, fontSize: '1.05rem' }}
              data-testid="submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{isSignup ? 'Create Account' : 'Sign In'} <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>

            {mode === 'login' && (
              <p className="text-center text-sm text-gray-500 mt-2">
                <button type="button" onClick={() => setForgot({ open: true, step: 'email', email, code: '', newPassword: '' })} className="hover:underline" style={{ color: THEME.purple }} data-testid="forgot-password-link">Forgot password?</button>
              </p>
            )}
          </form>
        )}

        {/* Mode toggle */}
        {!isAdminPortal && (
          <p className="text-center text-sm text-gray-400 mt-8" data-testid="auth-mode-toggle">
            {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={() => { setMode(isSignup ? 'login' : 'register'); setSignupStep('form'); setVerifyCode(''); }}
              className="font-bold"
              style={{ color: primary }}
            >
              {isSignup ? 'Sign in' : 'Create account'}
            </button>
          </p>
        )}
      </div>

      {/* Forgot password modal */}
      {forgot.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="forgot-password-modal">
          <div className="bg-[#0F1115] border border-white/10 rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Reset your password</h3>
              <button onClick={() => setForgot({ ...forgot, open: false })} className="text-gray-400 hover:text-white text-xl">×</button>
            </div>

            {forgot.step === 'email' && (
              <>
                <p className="text-sm text-gray-400">We'll email a 6-digit reset code.</p>
                <Input type="email" value={forgot.email} onChange={e => setForgot({ ...forgot, email: e.target.value })} placeholder="you@example.com" className="bg-[#05060A] border-white/10 text-white h-12 rounded-xl" data-testid="forgot-email-input" />
                <Button
                  onClick={async () => {
                    if (!forgot.email) return toast.error('Enter your email');
                    try {
                      await api.post('/api/auth/forgot-password/start', { identifier: forgot.email });
                      toast.success('If that email is registered, a code has been sent.');
                      setForgot({ ...forgot, step: 'verify', code: '', newPassword: '' });
                    } catch { toast.error('Could not send email'); }
                  }}
                  className="w-full h-12 rounded-xl font-bold"
                  style={{ background: PRIMARY_GRADIENT, color: primaryText }}
                  data-testid="forgot-send-btn"
                >Send reset code</Button>
              </>
            )}

            {forgot.step === 'verify' && (
              <>
                <p className="text-sm text-gray-400">Code sent to <span className="text-white">{forgot.email}</span>.</p>
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  value={forgot.code}
                  onChange={e => setForgot({ ...forgot, code: e.target.value.replace(/\D/g, '') })}
                  placeholder="Enter code sent to your email"
                  className={`text-center bg-[#05060A] border-white/10 text-white h-14 rounded-xl ${forgot.code ? 'tracking-[0.5em] text-xl' : 'tracking-normal text-sm'}`}
                  data-testid="forgot-code-input"
                />
                <Button
                  onClick={async () => {
                    if (forgot.code.length !== 6) return toast.error('Enter the 6-digit code');
                    toast.success('Now choose your new password.');
                    setForgot({ ...forgot, step: 'reset' });
                  }}
                  className="w-full h-12 rounded-xl font-bold"
                  style={{ background: PRIMARY_GRADIENT, color: primaryText }}
                  data-testid="forgot-verify-code-btn"
                >Verify code</Button>
              </>
            )}

            {forgot.step === 'reset' && (
              <>
                <p className="text-sm text-gray-400">Code verified for <span className="text-white">{forgot.email}</span>.</p>
                <Input type="password" value={forgot.newPassword} onChange={e => setForgot({ ...forgot, newPassword: e.target.value })} placeholder="New password (min 6 chars)" className="bg-[#05060A] border-white/10 text-white h-12 rounded-xl" data-testid="forgot-newpass-input" />
                <Button
                  onClick={async () => {
                    if ((forgot.newPassword || '').length < 6) return toast.error('Password must be at least 6 characters');
                    try {
                      await api.post('/api/auth/forgot-password/verify', {
                        identifier: forgot.email,
                        code: forgot.code,
                        new_password: forgot.newPassword,
                      });
                      toast.success('Password updated. Please sign in.');
                      setForgot({ open: false, step: 'email', email: '', code: '', newPassword: '' });
                    } catch (e) {
                      if (/code/i.test(e.response?.data?.detail || e.response?.data?.message || '')) {
                        setForgot({ ...forgot, step: 'verify', newPassword: '' });
                      }
                      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Reset failed');
                    }
                  }}
                  className="w-full h-12 rounded-xl font-bold"
                  style={{ background: PRIMARY_GRADIENT, color: primaryText }}
                  data-testid="forgot-submit-btn"
                >Reset password</Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
